import { Anchor, Badge, Box, Group, Paper, SimpleGrid, Skeleton, Stack, Table, Text, Title, type MantineColor } from '@mantine/core';
import { ConsoleRole, DeviceStatus, Feature, IdpMode, KeyState, OrgStatus, Product, UserStatus } from '@motionsafe/ms-console-api/common/v1/common_pb.js';
import { IconBell, IconListDetails } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { type ReactNode } from 'react';

import { api } from '../../api/client';
import { deviceCountsQuery, keysQuery, notificationsQuery, orgQuery, userCountsQuery } from '../../api/hooks';
import { devicesIn, usersIn } from '../../lib/counts';
import { OrgActions } from '../../components/license';
import { OrgAgreementCard } from '../../components/org-agreement';
import { SignInSetup } from '../../components/sign-in-setup';
import { EmptyState, PageHeader, type StatProps } from '../../components/page';
import { poolText, when } from '../../lib/format';
import { isStaff, isStaffRole, STAFF_ORG_SLUG } from '../../lib/roles';
import { Route as AppRoute, useOrgId, type AppSearch } from '../_app';

export const Route = createFileRoute('/_app/overview')({ component: Overview });

// The questions an administrator asks first: are we within our limits, is
// anything waiting on me, what changed lately. Numbers with a limit are
// fractions of it; states are the audit trail's words.
const outcomeColor = (o: string): MantineColor => (o === 'ok' ? 'success' : o === 'refused' ? 'danger' : 'warning');
const severityColor = (s: string): MantineColor => (s === 'critical' ? 'danger' : s === 'warning' ? 'warning' : 'instrument');

const HAIRLINE = '1px solid var(--mantine-color-default-border)';

// Action names read as sentences: `operator.invited` becomes "operator invited".
// The ones whose words do not read on their own are said in full.
const ACTION_WORDS: Record<string, string> = {
  'org.sso_sign_in_changed': 'sign-in method switched',
  'idp.setup_link_created': 'identity provider setup opened',
  'org.reopened': 'organization reopened',
  'user.operator_access_changed': 'operator access changed',
  'user.console_access_changed': 'console access changed',
  'seat.unassigned': 'seat unassigned',
  'device.assigned': 'device assigned',
  'device.reregistered': 'device approved after a reset',
  'device.takeover_refused': 'device approval refused: wrong code',
  'device.deleted': 'device deleted',
  'feature.changed': 'add-on or sharing override changed',
  'products.changed': 'products changed',
  'mapping.changed': 'group provisioning changed',
  'admin.login': 'console sign-in',
  'admin.login_failed': 'console sign-in refused',
  'operator.login': 'device sign-in',
  'operator.login_failed': 'device sign-in refused',
  'user.sign_in_paused': 'sign-in paused',
  'collection.shared': 'collection shared',
  'collection.share_ended': 'share ended',
  'collection.received': 'collection received',
  'collection.transfer_refused': 'transfer refused: from another organization',
  'collection.share_refused': 'share refused by another organization',
  'collection.transferred': 'collection transferred between organizations',
};
const actionLabel = (a: string) => ACTION_WORDS[a] ?? a.replace(/[._]/g, ' ');

function Overview() {
  const orgId = useOrgId();
  const { me } = AppRoute.useRouteContext();
  const staff = isStaff(me);
  const search: AppSearch = staff ? { org: orgId } : {};
  const org = useQuery(orgQuery(orgId));
  const devices = useQuery(deviceCountsQuery(orgId));
  // The organization key is MotionSafe's: only staff ask about it.
  const keys = useQuery({ ...keysQuery(orgId), enabled: staff });
  // MotionSafe's own administrators are its staff, of either kind.
  const staffOrg = org.data?.slug === STAFF_ORG_SLUG;
  const admins = useQuery({ ...userCountsQuery(orgId), enabled: org.data !== undefined });
  // The organization in view, for staff too; the inbox has the all-organizations view.
  const notes = useQuery(notificationsQuery(orgId, true));
  const recent = useQuery({ queryKey: ['audit', orgId, 'recent'], queryFn: () => api.audit.queryAudit({ orgId, pageSize: 8, actions: [] }).then((r) => r.events) });

  const o = org.data;
  const seats = o?.seatPool;
  const addOns = o?.features.filter((f) => f in ADD_ON_NAMES) ?? [];
  const onRecord = o?.devicesOnRecord;
  const current = keys.data?.find((k) => k.identity?.state === KeyState.CURRENT);
  const suspendedDevices = devicesIn(devices.data ?? [], (d) => d.status === DeviceStatus.SUSPENDED);
  const activeAdmins = admins.data ? usersIn(admins.data, (u) => u.status === UserStatus.ACTIVE && (staffOrg ? isStaffRole(u.consoleRole) : u.consoleRole === ConsoleRole.ORG_ADMIN)) : undefined;

  const stats: StatProps[] = [
    {
      label: seats?.unlimited ? 'Operators' : 'Seats',
      value: seats ? poolText(seats) : <NotSet />,
      hint: seats?.unlimited ? 'No seat pool applies to MotionSafe' : seats && seats.pending > 0 ? `${seats.pending} waiting for a seat` : 'Active operator accounts',
      tone: seats && !seats.unlimited && seats.count > 0 && (seats.pending > 0 || seats.used >= seats.count) ? 'warn' : 'neutral',
    },
    {
      label: 'Devices on record',
      value: onRecord ?? <NotSet />,
      hint: suspendedDevices > 0 ? `${suspendedDevices} suspended` : 'Active and suspended devices',
      tone: 'neutral',
    },
    ...(staff ? [{
      label: 'Organization key',
      value: current ? <span className="id">{current.identity!.generation}</span> : keys.isPending ? '…' : 'none',
      hint: current ? `fingerprint ${current.identity!.fingerprint}` : 'Nobody can sign in on a device until MotionSafe issues the first key',
      tone: current ? 'neutral' : 'bad',
    } satisfies StatProps] : []),
    {
      label: 'Administrators',
      value: activeAdmins ?? (admins.isPending ? '…' : <Dim>unknown</Dim>),
      hint: 'Can sign in here',
      tone: 'neutral',
    },
  ];

  return (
    <>
      <PageHeader
        title={o?.name ?? 'Overview'}
        // The line is held while the organization loads, so the page does not drop when it lands.
        description={o ? `Organization ${o.slug}. ${o.products.length ? productSentence(o.products.length) : 'No products enabled yet; MotionSafe enables them.'}` : '\u00a0'}
        stats={org.isPending ? 'loading' : stats}
        statsCount={stats.length}
      />

      <SimpleGrid cols={{ base: 1, lg: 5 }} spacing="lg" style={{ alignItems: 'start' }}>
        <Paper style={{ gridColumn: 'span 3' }} p={0}>
          <Group justify="space-between" px="lg" py="md">
            <Title order={3}>Recent activity</Title>
            <Anchor renderRoot={(p) => <Link {...p} to="/audit" search={search} />} size="sm">Open the audit trail</Anchor>
          </Group>
          {recent.data && recent.data.length === 0 && (
            <EmptyState compact icon={IconListDetails} title="Nothing recorded yet" body="Sign-ins, invitations, and every administrative change appear here as they happen." />
          )}
          {/* The action wraps and the time keeps its column, so a phone reads
              every row without scrolling sideways. */}
          {(recent.isPending || (recent.data && recent.data.length > 0)) && (
            <Table verticalSpacing="xs" aria-label="Recent audit events" aria-busy={recent.isPending} highlightOnHover={false}>
              <Table.Tbody>
                {recent.isPending && [0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                  <Table.Tr key={`loading-${i}`} aria-hidden="true">
                    <Table.Td pl="lg" style={{ width: '1%' }}><Skeleton height={20} width={44} radius="sm" /></Table.Td>
                    <Table.Td><Skeleton height={12} width="45%" my={3} /><Skeleton height={10} width="20%" my={3} /></Table.Td>
                    <Table.Td pr="lg"><Skeleton height={12} width={56} ml="auto" /></Table.Td>
                  </Table.Tr>
                ))}
                {recent.data?.map((e) => (
                  <Table.Tr key={e.id}>
                    <Table.Td pl="lg" style={{ width: '1%', whiteSpace: 'nowrap' }}><Badge color={outcomeColor(e.outcome)}>{e.outcome}</Badge></Table.Td>
                    <Table.Td>
                      <Text size="sm">{actionLabel(e.action)}</Text>
                      <Text size="xs" c="dimmed" lineClamp={1}>{e.actor?.name || 'system'}{e.target?.name ? ` · ${e.target.name}` : ''}</Text>
                    </Table.Td>
                    <Table.Td ta="right" pr="lg" className="num" style={{ width: '1%', whiteSpace: 'nowrap' }}><Text size="sm" c="dimmed">{when(e.occurredAt, { relative: true })}</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Paper>

        <Stack gap="lg" style={{ gridColumn: 'span 2' }}>
          {/* What the organization is licensed for. MotionSafe staff change
              it from here; the organization's own administrators read it
              and are told who can change it. */}
          <Paper p="lg">
            {/* The state sits with the title; seats and devices are counted in the strip above, so only the seat pool is said again. */}
            <Group justify="space-between" align="center" mb="md" wrap="nowrap">
              <Group gap="sm" align="center" wrap="nowrap">
                <Title order={3}>License</Title>
                {o && <Badge color={orgStatusColor(o.status)}>{OrgStatus[o.status].toLowerCase()}</Badge>}
              </Group>
              {staff && o && <OrgActions org={o} />}
            </Group>
            {o ? (
              <Box component="dl" m={0} style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 'var(--mantine-spacing-lg)', rowGap: 10 }}>
                <Term label={seats?.unlimited ? 'Operators' : 'Seats'}>{seats?.unlimited ? `${seats.used}, no pool` : poolText(seats, 'not set')}</Term>
                <Term label="Products">{o.products.length ? o.products.map(productName).join(', ') : <Text span inherit c="dimmed">none</Text>}</Term>
                <Term label="Add-ons">{addOns.length ? addOns.map(addOnName).join(', ') : <Text span inherit c="dimmed">none</Text>}</Term>
                <Term label="Sign-in">
                  {o.idpMode === IdpMode.SSO ? 'Single sign-on' : 'Password'}
                  {staff && (o.idpOrgId
                    ? <Text size="xs" c="dimmed" className="id" style={{ overflowWrap: 'anywhere' }}>{o.idpOrgId}</Text>
                    : <Text size="xs" c="dimmed">Not linked at the identity provider</Text>)}
                </Term>
              </Box>
            ) : (
              <Stack gap={10} aria-hidden="true">
                {[0, 1, 2, 3].map((i) => (
                  <Group key={i} gap="lg" wrap="nowrap"><Skeleton height={12} width={64} my={3} /><Skeleton height={12} width="45%" my={3} /></Group>
                ))}
              </Stack>
            )}
            <Text size="xs" c="dimmed" mt="md">
              {o ? `Since ${day(o.createdAt)}. ` : ''}{!staff && 'Set by MotionSafe under your agreement; your account manager can change it.'}
            </Text>
          </Paper>

          {/* The contract that stands in for MotionSafe's legal documents, when the organization signed one. */}
          {o && <OrgAgreementCard org={o} />}

          {/* Given by staff as an add-on; setting it up is the organization's own. */}
          {o?.features.includes(Feature.SSO) && <SignInSetup org={o} />}

          <Paper p={0}>
            <Group justify="space-between" px="lg" py="md">
              <Title order={3}>Needs attention</Title>
              <Anchor renderRoot={(p) => <Link {...p} to="/notifications" search={search} />} size="sm">All notifications</Anchor>
            </Group>
            {notes.isPending && [0, 1].map((i) => (
              <Group key={i} gap="xs" wrap="nowrap" align="flex-start" px="lg" py="sm" aria-hidden="true" style={{ borderTop: HAIRLINE }}>
                <Skeleton height={20} width={60} radius="sm" mt={2} />
                <Box flex={1}><Skeleton height={12} width="70%" my={4} /><Skeleton height={10} width="25%" my={3} /></Box>
              </Group>
            ))}
            {notes.data && notes.data.notifications.length === 0 && (
              <EmptyState compact icon={IconBell} title="Nothing unread" body="Refused sign-ins over a limit and devices seen where they should not be are reported here." />
            )}
            {notes.data && notes.data.notifications.length > 0 && (
              <Stack gap={0}>
                {notes.data.notifications.slice(0, 5).map((n) => (
                  <Box key={n.id} px="lg" py="sm" style={{ borderTop: HAIRLINE }}>
                    <Group gap="xs" wrap="nowrap" align="flex-start">
                      <Badge color={severityColor(n.severity)} mt={2} flex="none">{n.severity}</Badge>
                      <Box miw={0}>
                        <Text size="sm" fw={500}>{n.title}{n.count > 1 && <Text span size="xs" c="dimmed" className="num"> · {n.count} times</Text>}</Text>
                        <Text size="xs" c="dimmed" className="num">{when(n.lastAt, { relative: true })}</Text>
                      </Box>
                    </Group>
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>

          <Paper p="lg">
            <Title order={3} mb="sm">Policy</Title>
            {o?.policy ? (
              <Stack gap={6}>
                <Row label="Session length" value={`${o.policy.sessionTtlHours} h`} />
                <Row label="Works offline for" value={`${o.policy.sessionTtlHours + o.policy.offlineGraceHours} h`} />
                <Row label="2FA" value={o.policy.requireMfa ? 'required' : 'not required'} />
                <Row label="Decrypted sharing" value={o.features.includes(Feature.DECRYPTED_SHARING) && (o.policy.decryptedSharing ?? true) ? 'on' : 'off'} />
              </Stack>
            ) : (
              <Stack gap={6} aria-hidden="true">
                {[0, 1, 2, 3].map((i) => (
                  <Group key={i} justify="space-between" wrap="nowrap"><Skeleton height={12} width="40%" my={3} /><Skeleton height={12} width="20%" my={3} /></Group>
                ))}
              </Stack>
            )}
            <Anchor renderRoot={(p) => <Link {...p} to="/policy" search={search} />} size="sm" display="inline-block" mt="md">Review policy</Anchor>
          </Paper>
        </Stack>
      </SimpleGrid>

    </>
  );
}

const orgStatusColor = (s: OrgStatus): MantineColor => (s === OrgStatus.ACTIVE ? 'success' : s === OrgStatus.UNSPECIFIED ? 'gray' : 'danger');
const productName = (p: Product) => (p === Product.INSIGHTS ? 'Insights' : p === Product.PRIVACY ? 'Privacy' : Product[p].toLowerCase());
// The add-ons an organization buys; the sharing overrides in `features` are not among them.
const ADD_ON_NAMES: Partial<Record<Feature, string>> = { [Feature.SSO]: 'Single sign-on', [Feature.DIRECTORY_SYNC]: 'Group provisioning' };
const addOnName = (f: Feature) => ADD_ON_NAMES[f] ?? Feature[f].toLowerCase();

// A value that is not there yet reads as a word at the secondary ink.
function Dim({ children }: { children: ReactNode }) {
  return <Text span inherit c="dimmed">{children}</Text>;
}

function NotSet() {
  return <Dim>not set</Dim>;
}

// One line of a card's facts: the term in the secondary ink, what it is beside it, both read from the left.
function Term({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <Text component="dt" size="sm" c="dimmed">{label}</Text>
      <Text component="dd" size="sm" m={0} className="num">{children}</Text>
    </>
  );
}

const day = (ts: { seconds: bigint } | undefined) => (ts ? new Date(Number(ts.seconds) * 1000).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '');

function Row({ label, value }: { label: string; value: string }) {
  return (
    <Group justify="space-between" gap="md" wrap="nowrap">
      <Text size="sm" c="dimmed">{label}</Text>
      <Text size="sm" className="num">{value}</Text>
    </Group>
  );
}

function productSentence(n: number): string {
  return n === 2 ? 'Privacy and Insights are enabled.' : 'One product is enabled.';
}
